interface DialogState {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  loading: boolean;
}

interface DialogOptions {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

type DialogListener = (state: DialogState) => void;

let dialogState: DialogState = {
  open: false,
  title: "",
  description: "",
  confirmLabel: "Confirm",
  cancelLabel: "Cancel",
  loading: false,
};

let dialogListeners: DialogListener[] = [];
let resolveRef: (() => void) | null = null;
let rejectRef: ((reason?: unknown) => void) | null = null;

export const dialogService = {
  confirm({ title, description, confirmLabel = "Confirm", cancelLabel = "Cancel" }: DialogOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      resolveRef = resolve;
      rejectRef = reject;
      dialogState = {
        open: true,
        title,
        description,
        confirmLabel,
        cancelLabel,
        loading: false,
      };
      dialogListeners.forEach((listener) => listener(dialogState));
    });
  },

  async handleConfirm(): Promise<void> {
    if (!resolveRef) return;

    dialogState = { ...dialogState, loading: true };
    dialogListeners.forEach((listener) => listener(dialogState));

    try {
      const resolve = resolveRef;
      resolveRef = null;
      rejectRef = null;
      resolve();
      dialogState = { ...dialogState, open: false, loading: false };
      dialogListeners.forEach((listener) => listener(dialogState));
    } catch (error) {
      dialogState = { ...dialogState, loading: false };
      dialogListeners.forEach((listener) => listener(dialogState));
      throw error;
    }
  },

  handleCancel(): void {
    if (rejectRef) {
      const reject = rejectRef;
      resolveRef = null;
      rejectRef = null;
      reject(new Error("User cancelled"));
    }
    dialogState = { ...dialogState, open: false, loading: false };
    dialogListeners.forEach((listener) => listener(dialogState));
  },

  handleClose(): void {
    if (!dialogState.loading) {
      this.handleCancel();
    }
  },

  subscribe(listener: DialogListener): () => void {
    dialogListeners.push(listener);
    return () => {
      dialogListeners = dialogListeners.filter((l) => l !== listener);
    };
  },
};